import { OrderConstant } from 'src/constants';
import {
  OrderActionDto,
  OrderDto,
  OrderDurationDto,
  OrderTypeDto,
  QuoteDto,
  TradingBlockOrderStatusEnum,
  TradingBlockOrderTypeNumberEnum,
} from 'src/dtos';
import {
  Order,
  OrderAction,
  OrderActionLabel,
  OrderDuration,
  OrderDurationLabel,
  OrderStatus,
  OrderType,
  OrderTypeLabel,
} from 'src/models';

import { FromTradingBlockServerEvent, FromTradingBlockServerEventDto } from '../events/FromTradingBlockServerEvent';
import { Logger } from '../utils/Logger';

const incompleteMarketOrderPrice = (order: OrderDto, lastValue: number = 0): number => {
  let price = lastValue;

  if (
    order.OrderStatus === TradingBlockOrderStatusEnum.Cancelled ||
    order.OrderStatus === TradingBlockOrderStatusEnum.Rejected ||
    order.OrderStatus === TradingBlockOrderStatusEnum.Expired
  ) {
    price = 0; // NOTE: TB market order price is not saved for unfilled orders;
  }

  return price;
};

const calculatePrice = (order: OrderDto, lastValue: number = 0): number => {
  if (order.AverageFillPrice !== undefined && order.AverageFillPrice > 0) {
    return order.AverageFillPrice;
  }

  if (order.OrderStatus === TradingBlockOrderStatusEnum.Filled) {
    return order.AverageFillPrice ?? 0;
  }

  try {
    switch (order.OrderType) {
      case TradingBlockOrderTypeNumberEnum.Undefined: {
        return order.AverageFillPrice ?? 0;
      }

      case TradingBlockOrderTypeNumberEnum.Market: {
        return incompleteMarketOrderPrice(order, lastValue);
      }

      case TradingBlockOrderTypeNumberEnum.Limit: {
        return order.Price ?? 0;
      }

      case TradingBlockOrderTypeNumberEnum.Stop_Market: {
        return order.Stop ?? 0;
      }

      case TradingBlockOrderTypeNumberEnum.Stop_Limit: {
        return order.Price ?? 0;
      }

      case TradingBlockOrderTypeNumberEnum.Market_On_Close: {
        return incompleteMarketOrderPrice(order, lastValue);
      }

      default: {
        throw new Error(`Expected order type "TradingBlockOrderTypeNumberEnum". Received: "${order?.OrderType}"`);
      }
    }
  } catch (error) {
    Logger.error('Error calculating order price:', error);
    Logger.info('Fallback: return zero');

    return 0;
  }
};

const isPartialOrder = ({ quantity, filledQuantity }: { quantity: number; filledQuantity: number }): boolean =>
  Boolean(filledQuantity < quantity && filledQuantity > 0);

function addTotalCostToPartialOrder<T extends Order | FromTradingBlockServerEvent.OrderUpdateEventPayload>(
  order: T,
): T {
  let totalCost = order.filledQuantity * order.price;

  if (order.action?.isBuy) {
    totalCost = -(totalCost + order.commission);
  }

  if (order.action?.isSell) {
    totalCost = totalCost - order.commission;
  }

  return { ...order, totalCost };
}

function addTotalCostToCompletedOrder<T extends Order | FromTradingBlockServerEvent.OrderUpdateEventPayload>(
  order: T,
): T {
  let totalCost = order.quantity * order.price;

  if (order.action?.isBuy) {
    totalCost = -(totalCost + order.commission);
  }

  if (order.action?.isSell) {
    totalCost = totalCost - order.commission;
  }

  return { ...order, totalCost };
}

function addTotalCostToIncompleteOrder<T extends Order | FromTradingBlockServerEvent.OrderUpdateEventPayload>(
  order: T,
): T {
  let totalCost = order.quantity * order.price;

  if (order.isPartial) {
    return addTotalCostToPartialOrder(order);
  }

  if (order.action?.isBuy) {
    totalCost = -(totalCost + OrderConstant.ESTIMATED_SERVICE_FEE);
  }

  if (order.action?.isSell) {
    totalCost = totalCost - OrderConstant.ESTIMATED_SERVICE_FEE;
  }

  return { ...order, totalCost };
}

const addTotalCost = (order: Order): Order => {
  let totalCost = order.quantity * order.price;

  if (totalCost === 0) {
    return order;
  }

  return order.status.isCompleted ? addTotalCostToCompletedOrder(order) : addTotalCostToIncompleteOrder(order);
};

function addCommission<T extends Order | FromTradingBlockServerEvent.OrderUpdateEventPayload>(order: T): T {
  if (order.status.isCompleted || order.isPartial) {
    return order;
  }

  return { ...order, commission: OrderConstant.ESTIMATED_SERVICE_FEE };
}

export const mapOrderDtoToModel = (dto: OrderDto, quote?: QuoteDto): Order => {
  let result: Order = {
    id: dto.OrderId.toString(),
    updatedAt: dto.Date,
    quantity: dto.Quantity,
    filledQuantity: dto.FillQuantity ?? 0,
    isPartial: isPartialOrder({ quantity: dto.Quantity ?? 0, filledQuantity: dto.FillQuantity ?? 0 }),
    price: calculatePrice(dto, quote?.pricedata.last),
    totalCost: 0,
    type: new OrderType(dto.OrderType),
    symbol: dto.UnderlyingSymbol ?? '',
    description: quote?.longname ?? dto.UnderlyingSymbol ?? '',
    action: new OrderAction(dto.Legs?.[0]?.Action),
    status: new OrderStatus(dto.OrderStatus),
    duration: new OrderDuration(dto.Duration),
    commission: dto.CommissionAssessed,
    limit: dto.Price,
    stop: dto.Stop,
  };
  result = addTotalCost(result);
  result = addCommission(result);

  return result;
};

export const mapGetOrdersListResponseDtoToModel = (dtos: OrderDto[], quotes: QuoteDto[]): Order[] =>
  dtos.map(orderDto =>
    mapOrderDtoToModel(
      orderDto,
      quotes.find(aQuote => aQuote.symbol === orderDto.UnderlyingSymbol),
    ),
  );

export const mapOrderUpdateEventDtoToModel = (
  dto: FromTradingBlockServerEventDto.OrderUpdateEventDto,
): FromTradingBlockServerEvent.OrderUpdateEvent => {
  const result: FromTradingBlockServerEvent.OrderUpdateEvent = {
    type: FromTradingBlockServerEvent.EventType.ORDER_UPDATE,
    payload: {
      updatedAt: dto[4],
      symbol: dto[5],
      quantity: Number(dto[6]),
      filledQuantity: Number(dto[7]),
      price: Number(dto[8]),
      action: new OrderAction(Number(dto[9])),
      status: new OrderStatus(Number(dto[10])),
      isPartial: isPartialOrder({ quantity: Number(dto[6]), filledQuantity: Number(dto[7]) }),
      commission: Number(dto[15]),
    },
  };

  if (result.payload.status.isCompleted) {
    result.payload = addTotalCostToCompletedOrder(result.payload);
  }

  if (result.payload.isPartial) {
    result.payload = addTotalCostToPartialOrder(result.payload);
  }
  result.payload = addCommission(result.payload);

  if (dto[3] !== '0') {
    result.payload.id = dto[3];
  }

  return result;
};

export const mapOrderTypeDtoToLabel = (type: OrderTypeDto): OrderTypeLabel => {
  switch (type) {
    case OrderTypeDto.Market:
      return OrderTypeLabel.Market;
    case OrderTypeDto.Limit:
      return OrderTypeLabel.Limit;
    case OrderTypeDto.StopMarket:
      return OrderTypeLabel.StopMarket;
    case OrderTypeDto.StopLimit:
      return OrderTypeLabel.StopLimit;
    case OrderTypeDto.MarketOnClose:
      return OrderTypeLabel.MarketOnClose;
    default:
      return OrderTypeLabel.Market;
  }
};

export const mapOrderActionDtoToLabel = (action: OrderActionDto): OrderActionLabel => {
  return action === OrderActionDto.Buy ? OrderActionLabel.Buy : OrderActionLabel.Sell;
};

export const mapOrderDurationDtoToLabel = (duration: OrderDurationDto): OrderDurationLabel => {
  return duration === OrderDurationDto.Day ? OrderDurationLabel.Day : OrderDurationLabel.GTC;
};
